import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { FiAlertCircle, FiCopy } from "react-icons/fi";
import { toast } from "react-toastify";
import useCallCenterT from "../../hooks/useCallCenterT";
import { getCallCenterBaseUrl } from "../../service/CallCenterApi";
import {
  getOrganizationWhatsAppConfig,
  updateOrganizationWhatsAppConfig,
} from "../../service/CallCenterWhatsAppConfig";
import {
  FILTER_FIELD_CLASS,
  FILTER_LABEL_CLASS,
  LISTING_CONTAINER_CLASS,
  LISTING_PAGE_CLASS,
  LISTING_PRIMARY_BUTTON_CLASS,
} from "../../components/call-center/listing/listingStyles";

const PROVIDERS = {
  META_DIRECT: "META_DIRECT",
  FLOWMATICAL: "FLOWMATICAL",
};

const CHECKBOX_CLASS =
  "h-4 w-4 rounded border-slate-300 text-[#2B7FFF] accent-[#2B7FFF] focus:ring-[#2B7FFF]";

const emptyValues = {
  enabled: false,
  provider: PROVIDERS.META_DIRECT,
  phoneNumberId: "",
  accessToken: "",
  webhookVerifyToken: "",
  appSecret: "",
  apiVersion: "v21.0",
  flowmaticalBaseUrl: "https://flowmatical.com",
  flowmaticalApiToken: "",
  flowmaticalWebhookSecret: "",
};

const SettingsSkeleton = () => (
  <div className="space-y-5" aria-hidden="true">
    <div className="h-5 w-48 animate-pulse rounded bg-slate-100" />
    <div className="h-11 animate-pulse rounded-xl bg-slate-100" />
    <div className="h-11 animate-pulse rounded-xl bg-slate-100" />
    <div className="h-11 animate-pulse rounded-xl bg-slate-100" />
    <div className="h-11 w-40 animate-pulse rounded-xl bg-slate-100" />
  </div>
);

const WebhookUrlPanel = ({ label, url, hint, copyLabel, copiedLabel }) => {
  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(copiedLabel);
    } catch {
      toast.error(copyLabel);
    }
  };

  if (!url) return null;

  return (
    <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4 text-sm text-emerald-900">
      <p className="font-medium">{label}</p>
      <div className="mt-2 flex flex-wrap items-start gap-2">
        <code className="min-w-0 flex-1 break-all rounded-lg bg-white/70 px-2 py-1.5 text-xs ring-1 ring-emerald-100">
          {url}
        </code>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-emerald-200 bg-white px-3 text-xs font-semibold text-emerald-800 transition hover:bg-emerald-100"
        >
          <FiCopy className="h-3.5 w-3.5" />
          {copyLabel}
        </button>
      </div>
      {hint ? <p className="mt-2 text-xs leading-5 text-emerald-800">{hint}</p> : null}
    </div>
  );
};

const CallCenterWhatsAppSettingsPage = () => {
  const t = useCallCenterT();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [initialValues, setInitialValues] = useState(emptyValues);
  const [organizationId, setOrganizationId] = useState(null);
  const [flags, setFlags] = useState({
    accessTokenConfigured: false,
    webhookVerifyTokenConfigured: false,
    appSecretConfigured: false,
    flowmaticalApiTokenConfigured: false,
    flowmaticalWebhookSecretConfigured: false,
  });

  const schema = useMemo(
    () =>
      Yup.object({
        enabled: Yup.boolean(),
        provider: Yup.string().oneOf([PROVIDERS.META_DIRECT, PROVIDERS.FLOWMATICAL]),
        phoneNumberId: Yup.string().trim().when(["enabled", "provider"], {
          is: (enabled, provider) => enabled && provider === PROVIDERS.META_DIRECT,
          then: (field) => field.required(t("ccWhatsAppPhoneNumberIdRequired")),
        }),
        accessToken: Yup.string().trim(),
        webhookVerifyToken: Yup.string().trim(),
        appSecret: Yup.string().trim(),
        apiVersion: Yup.string().trim().when(["enabled", "provider"], {
          is: (enabled, provider) => enabled && provider === PROVIDERS.META_DIRECT,
          then: (field) => field.required(t("ccWhatsAppApiVersionRequired")),
        }),
        flowmaticalBaseUrl: Yup.string().trim().when(["enabled", "provider"], {
          is: (enabled, provider) => enabled && provider === PROVIDERS.FLOWMATICAL,
          then: (field) => field.required(t("ccWhatsAppFlowmaticalBaseUrlRequired")),
        }),
        flowmaticalApiToken: Yup.string().trim(),
        flowmaticalWebhookSecret: Yup.string().trim(),
      }),
    [t]
  );

  const apiBase = useMemo(() => getCallCenterBaseUrl().replace(/\/$/, ""), []);

  const metaWebhookUrl = useMemo(() => `${apiBase}/api/whatsapp/webhook`, [apiBase]);

  const flowmaticalWebhookUrl = useMemo(() => {
    if (!organizationId) return "";
    return `${apiBase}/webhooks/flowmatical/${organizationId}`;
  }, [apiBase, organizationId]);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError("");
      const data = await getOrganizationWhatsAppConfig();
      setOrganizationId(data?.organizationId ?? null);
      setInitialValues({
        enabled: Boolean(data?.enabled),
        provider: data?.provider || PROVIDERS.META_DIRECT,
        phoneNumberId: data?.phoneNumberId || "",
        accessToken: "",
        webhookVerifyToken: "",
        appSecret: "",
        apiVersion: data?.apiVersion || "v21.0",
        flowmaticalBaseUrl: data?.flowmaticalBaseUrl || "https://flowmatical.com",
        flowmaticalApiToken: "",
        flowmaticalWebhookSecret: "",
      });
      setFlags({
        accessTokenConfigured: Boolean(data?.accessTokenConfigured),
        webhookVerifyTokenConfigured: Boolean(data?.webhookVerifyTokenConfigured),
        appSecretConfigured: Boolean(data?.appSecretConfigured),
        flowmaticalApiTokenConfigured: Boolean(data?.flowmaticalApiTokenConfigured),
        flowmaticalWebhookSecretConfigured: Boolean(data?.flowmaticalWebhookSecretConfigured),
      });
    } catch {
      setLoadError(t("ccWhatsAppLoadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        enabled: Boolean(values.enabled),
        provider: values.provider,
        apiVersion: values.apiVersion.trim() || "v21.0",
      };

      if (values.provider === PROVIDERS.META_DIRECT) {
        payload.phoneNumberId = values.phoneNumberId.trim();
        if (values.accessToken.trim()) payload.accessToken = values.accessToken.trim();
        if (values.webhookVerifyToken.trim()) {
          payload.webhookVerifyToken = values.webhookVerifyToken.trim();
        }
        if (values.appSecret.trim()) payload.appSecret = values.appSecret.trim();
      } else {
        payload.flowmaticalBaseUrl = values.flowmaticalBaseUrl.trim();
        if (values.flowmaticalApiToken.trim()) {
          payload.flowmaticalApiToken = values.flowmaticalApiToken.trim();
        }
        if (values.flowmaticalWebhookSecret.trim()) {
          payload.flowmaticalWebhookSecret = values.flowmaticalWebhookSecret.trim();
        }
      }

      const updated = await updateOrganizationWhatsAppConfig(payload);
      setOrganizationId(updated?.organizationId ?? organizationId);
      setFlags({
        accessTokenConfigured: Boolean(updated?.accessTokenConfigured),
        webhookVerifyTokenConfigured: Boolean(updated?.webhookVerifyTokenConfigured),
        appSecretConfigured: Boolean(updated?.appSecretConfigured),
        flowmaticalApiTokenConfigured: Boolean(updated?.flowmaticalApiTokenConfigured),
        flowmaticalWebhookSecretConfigured: Boolean(updated?.flowmaticalWebhookSecretConfigured),
      });
      toast.success(t("ccWhatsAppSaveSuccess"));
    } catch (error) {
      toast.error(error.message || t("ccWhatsAppSaveFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={LISTING_PAGE_CLASS}>
      <div className={`${LISTING_CONTAINER_CLASS} max-w-3xl`}>
        <header className="mb-6">
          <h1 className="text-[28px] font-bold tracking-tight text-[#111827]">
            {t("ccWhatsAppSettingsTitle")}
          </h1>
          <p className="mt-1 text-sm text-[#6b7280]">{t("ccWhatsAppSettingsSubtitle")}</p>
        </header>

        <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm sm:p-8">
          {loading ? (
            <SettingsSkeleton />
          ) : loadError ? (
            <div className="flex flex-col items-center gap-4 py-8 text-center">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-600">
                <FiAlertCircle className="h-6 w-6" />
              </div>
              <p className="text-sm text-red-700">{loadError}</p>
              <button
                type="button"
                onClick={loadConfig}
                className={LISTING_PRIMARY_BUTTON_CLASS}
              >
                {t("ccRefresh")}
              </button>
            </div>
          ) : (
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={schema}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, isSubmitting }) => (
                <Form className="space-y-5">
                  <label className="flex items-center gap-3 text-sm font-medium text-[#111827]">
                    <Field type="checkbox" name="enabled" className={CHECKBOX_CLASS} />
                    {t("ccWhatsAppEnabled")}
                  </label>

                  <div>
                    <label className={FILTER_LABEL_CLASS}>{t("ccWhatsAppProvider")}</label>
                    <Field as="select" name="provider" className={FILTER_FIELD_CLASS}>
                      <option value={PROVIDERS.META_DIRECT}>
                        {t("ccWhatsAppProviderMetaDirect")}
                      </option>
                      <option value={PROVIDERS.FLOWMATICAL}>
                        {t("ccWhatsAppProviderFlowmatical")}
                      </option>
                    </Field>
                  </div>

                  {values.provider === PROVIDERS.META_DIRECT ? (
                    <>
                      <div>
                        <label className={FILTER_LABEL_CLASS}>{t("ccWhatsAppPhoneNumberId")}</label>
                        <Field
                          name="phoneNumberId"
                          className={`${FILTER_FIELD_CLASS} ${
                            touched.phoneNumberId && errors.phoneNumberId ? "border-red-500" : ""
                          }`}
                          placeholder="123456789012345"
                        />
                        {touched.phoneNumberId && errors.phoneNumberId && (
                          <p className="mt-1 text-xs text-red-600">{errors.phoneNumberId}</p>
                        )}
                      </div>

                      <div>
                        <label className={FILTER_LABEL_CLASS}>{t("ccWhatsAppAccessToken")}</label>
                        <Field
                          type="password"
                          name="accessToken"
                          className={FILTER_FIELD_CLASS}
                          placeholder={
                            flags.accessTokenConfigured ? t("ccWhatsAppSecretConfiguredHint") : ""
                          }
                        />
                      </div>

                      <div>
                        <label className={FILTER_LABEL_CLASS}>
                          {t("ccWhatsAppWebhookVerifyToken")}
                        </label>
                        <Field
                          type="password"
                          name="webhookVerifyToken"
                          className={FILTER_FIELD_CLASS}
                          placeholder={
                            flags.webhookVerifyTokenConfigured
                              ? t("ccWhatsAppSecretConfiguredHint")
                              : ""
                          }
                        />
                      </div>

                      <div>
                        <label className={FILTER_LABEL_CLASS}>{t("ccWhatsAppAppSecret")}</label>
                        <Field
                          type="password"
                          name="appSecret"
                          className={FILTER_FIELD_CLASS}
                          placeholder={
                            flags.appSecretConfigured ? t("ccWhatsAppSecretConfiguredHint") : ""
                          }
                        />
                      </div>

                      <div>
                        <label className={FILTER_LABEL_CLASS}>{t("ccWhatsAppApiVersion")}</label>
                        <Field
                          name="apiVersion"
                          className={`${FILTER_FIELD_CLASS} ${
                            touched.apiVersion && errors.apiVersion ? "border-red-500" : ""
                          }`}
                        />
                        {touched.apiVersion && errors.apiVersion && (
                          <p className="mt-1 text-xs text-red-600">{errors.apiVersion}</p>
                        )}
                      </div>

                      <WebhookUrlPanel
                        label={t("ccWhatsAppMetaWebhookUrlLabel")}
                        url={metaWebhookUrl}
                        hint={t("ccWhatsAppMetaWebhookHint")}
                        copyLabel={t("ccWhatsAppCopyUrl")}
                        copiedLabel={t("ccWhatsAppUrlCopied")}
                      />
                    </>
                  ) : (
                    <>
                      <div>
                        <label className={FILTER_LABEL_CLASS}>
                          {t("ccWhatsAppFlowmaticalBaseUrl")}
                        </label>
                        <Field
                          name="flowmaticalBaseUrl"
                          className={`${FILTER_FIELD_CLASS} ${
                            touched.flowmaticalBaseUrl && errors.flowmaticalBaseUrl
                              ? "border-red-500"
                              : ""
                          }`}
                          placeholder="https://flowmatical.com"
                        />
                        {touched.flowmaticalBaseUrl && errors.flowmaticalBaseUrl && (
                          <p className="mt-1 text-xs text-red-600">{errors.flowmaticalBaseUrl}</p>
                        )}
                      </div>

                      <div>
                        <label className={FILTER_LABEL_CLASS}>
                          {t("ccWhatsAppFlowmaticalApiToken")}
                        </label>
                        <Field
                          type="password"
                          name="flowmaticalApiToken"
                          className={FILTER_FIELD_CLASS}
                          placeholder={
                            flags.flowmaticalApiTokenConfigured
                              ? t("ccWhatsAppSecretConfiguredHint")
                              : ""
                          }
                        />
                      </div>

                      <div>
                        <label className={FILTER_LABEL_CLASS}>
                          {t("ccWhatsAppFlowmaticalWebhookSecret")}
                        </label>
                        <Field
                          type="password"
                          name="flowmaticalWebhookSecret"
                          className={FILTER_FIELD_CLASS}
                          placeholder={
                            flags.flowmaticalWebhookSecretConfigured
                              ? t("ccWhatsAppSecretConfiguredHint")
                              : ""
                          }
                        />
                      </div>

                      <WebhookUrlPanel
                        label={t("ccWhatsAppFlowmaticalWebhookUrlLabel")}
                        url={flowmaticalWebhookUrl}
                        hint={t("ccWhatsAppFlowmaticalWebhookHint")}
                        copyLabel={t("ccWhatsAppCopyUrl")}
                        copiedLabel={t("ccWhatsAppUrlCopied")}
                      />
                    </>
                  )}

                  <p className="text-xs leading-5 text-slate-400">{t("ccWhatsAppSettingsHint")}</p>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`${LISTING_PRIMARY_BUTTON_CLASS} !bg-emerald-600 hover:!bg-emerald-700 disabled:opacity-60`}
                  >
                    {isSubmitting ? t("ccSaving") : t("ccWhatsAppSave")}
                  </button>
                </Form>
              )}
            </Formik>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallCenterWhatsAppSettingsPage;
